import { useMemo, useRef } from "react"
import { useFilterQueryParams } from "../../hooks/filter_query_params"
import { useMapViewData } from "../../hooks/map_view_data"

export function useFlowDetails() {
  const { campusFlow, flowBuilding } = useFilterQueryParams()
  const { campusFlowContent, resources } = useMapViewData()
  const { getBuilding } = resources

  // Stabilize getBuilding ref to avoid defeating useMemo
  const getBuildingRef = useRef(getBuilding)
  getBuildingRef.current = getBuilding

  return useMemo(() => {
    if (!campusFlow || !flowBuilding) return null

    const getName = id => getBuildingRef.current?.(id)?.name || id

    const filteredRoutes = campusFlowContent?.filteredRoutes ?? []
    const displayVolumeFn = campusFlowContent?.displayVolumeFn

    if (!filteredRoutes.length) {
      return {
        buildingName: getName(flowBuilding),
        inflow: 0,
        outflow: 0,
        topArrivals: [],
        topDepartures: [],
      }
    }

    const getVolume = route => displayVolumeFn ? displayVolumeFn(route) : (route.semester_data?.volume ?? 0)

    let inflow = 0
    let outflow = 0
    const arrivalMap = {}
    const departureMap = {}

    for (const route of filteredRoutes) {
      const vol = getVolume(route)
      if (vol === 0) continue  // Skip routes with zero trips

      if (route.end_building_id === flowBuilding) {
        inflow += vol
        arrivalMap[route.start_building_id] = (arrivalMap[route.start_building_id] || 0) + vol
      }
      if (route.start_building_id === flowBuilding) {
        outflow += vol
        departureMap[route.end_building_id] = (departureMap[route.end_building_id] || 0) + vol
      }
    }

    const sortByVolume = map =>
      Object.entries(map)
        .map(([id, volume]) => ({ name: getName(id), volume }))
        .sort((a, b) => b.volume - a.volume)
        .slice(0, 3)

    return {
      buildingName: getName(flowBuilding),
      inflow,
      outflow,
      topArrivals: sortByVolume(arrivalMap),
      topDepartures: sortByVolume(departureMap),
    }
  }, [campusFlow, flowBuilding, campusFlowContent])
}
